import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import VoucherTypeSelector from "./components/VoucherTypeSelector";
import VoucherHeaderFields from "./components/VoucherHeaderFields";
import VoucherTable from "./components/VoucherTable";
import VoucherTotals from "./components/VoucherTotals";
import VoucherNarration from "./components/VoucherNarration";
import VoucherFooterActions from "./components/VoucherFooterActions";
import LedgerSearchModal from "./components/LedgerSearchModal";
import VoucherModifySelector from "./components/VoucherModifySelector";
import VoucherSearchModal from "./components/VoucherSearchModal";
import DeleteConfirmModal from "../../Component/DeleteModal/DeleteConfirmModal";
import {
  VOUCHER_TYPES,
  INITIAL_VOUCHER_STATE,
  isSalesType,
  normalizeVoucherType,
} from "./data/dummyVoucherData";
import "./VoucherEntry.css";

const DESIRED_VOUCHER_ORDER = [
  "payment",
  "receipt",
  "journal",
  "contra",
  "purchase",
  "debit_note",
  "credit_note",
];

const createInitialRows = (vType = "payment") => {
  const isReceipt = String(vType).toLowerCase().trim().replace(/[\s-]+/g, "_") === "receipt";
  const firstType = isReceipt ? "Cr" : "Dr";
  const secondType = isReceipt ? "Dr" : "Cr";
  return [
    { id: 1, type: firstType, headCode: null, ledger: "", dr: "", cr: "" },
    { id: 2, type: secondType, headCode: null, ledger: "", dr: "", cr: "" },
  ];
};

export default function VoucherEntry() {
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.id || localStorage.getItem("companyId") || "4211";
  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Determine if running in Modify Mode (via URL path or query param)
  const isModifyMode =
    location.pathname.toLowerCase().includes("modify") ||
    searchParams.get("mode") === "modify";

  // Dynamic voucher types loaded from backend API (with Sales excluded)
  const [voucherTypes, setVoucherTypes] = useState(VOUCHER_TYPES);

  // Dynamic ledgers loaded from Ledger API (HEAD_MAST)
  const [ledgers, setLedgers] = useState([]);
  const [isLedgersLoading, setIsLedgersLoading] = useState(false);

  // Preloaded Next Voucher Numbers map for instant switching (0ms delay)
  const [voucherNumbersMap, setVoucherNumbersMap] = useState({});

  // Main form states
  const [voucherType, setVoucherType] = useState(INITIAL_VOUCHER_STATE.voucherType);
  const [voucherNo, setVoucherNo] = useState(INITIAL_VOUCHER_STATE.voucherNo);
  const [referenceNo, setReferenceNo] = useState(INITIAL_VOUCHER_STATE.referenceNo);
  const [voucherDate, setVoucherDate] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [narration, setNarration] = useState("");
  const [rows, setRows] = useState(() => createInitialRows(INITIAL_VOUCHER_STATE.voucherType));

  // UI modals & status
  const [isSaving, setIsSaving] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [activeRowForSearch, setActiveRowForSearch] = useState(0);

  // Modify Mode specific states
  const [existingVouchers, setExistingVouchers] = useState([]);
  const [isLoadingVouchersList, setIsLoadingVouchersList] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState("");
  const [loadedVoucherId, setLoadedVoucherId] = useState(null);
  const [loadedVoucherData, setLoadedVoucherData] = useState(null);
  const [isLoadingVoucher, setIsLoadingVoucher] = useState(false);
  const [searchVoucherModalOpen, setSearchVoucherModalOpen] = useState(false);
  const [initialVoucherSnapshot, setInitialVoucherSnapshot] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const loadedKeyRef = useRef(null);

  // Map current voucher type to numeric VType
  const currentVTypeObj = useMemo(() => {
    return (
      voucherTypes.find(
        (v) =>
          v.id === voucherType ||
          v.vtype === voucherType ||
          (typeof voucherType === "string" && v.name?.toLowerCase() === voucherType.toLowerCase())
      ) || voucherTypes[0]
    );
  }, [voucherTypes, voucherType]);

  const currentVTypeNum = currentVTypeObj?.vtype || 3;

  // 1. Fetch Voucher Types from API (ordered: payment, receipt, journal, contra, purchase, debit_note, credit_note)
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await axios.get(`${BASE_API_URL}/vouchers/types`);
        if (res.data?.success && Array.isArray(res.data.data)) {
          const mapped = res.data.data
            .filter((t) => !isSalesType(t))
            .map(normalizeVoucherType);

          // Strictly order by DESIRED_VOUCHER_ORDER (Payment, Receipt, Journal, Contra, Purchase, Debit Note, Credit Note)
          const ordered = mapped
            .filter((t) => {
              const key = String(t.id || t.name || "").toLowerCase().trim().replace(/[\s-]+/g, "_");
              return DESIRED_VOUCHER_ORDER.includes(key);
            })
            .sort((a, b) => {
              const aKey = String(a.id || a.name || "").toLowerCase().trim().replace(/[\s-]+/g, "_");
              const bKey = String(b.id || b.name || "").toLowerCase().trim().replace(/[\s-]+/g, "_");
              return DESIRED_VOUCHER_ORDER.indexOf(aKey) - DESIRED_VOUCHER_ORDER.indexOf(bKey);
            });

          const finalTypes = ordered.length > 0 ? ordered : mapped;
          if (finalTypes.length > 0) {
            setVoucherTypes(finalTypes);
            if (!finalTypes.some((t) => t.id === voucherType)) {
              setVoucherType(finalTypes[0].id);
              setRows(createInitialRows(finalTypes[0].id));
            }
          }
        }
      } catch (err) {
        console.warn("Could not load voucher types from API, using defaults:", err);
      }
    };
    fetchTypes();
  }, [BASE_API_URL, voucherType]);

  // 2. Fetch Ledgers from API for current company
  useEffect(() => {
    const fetchLedgers = async () => {
      if (!companyId) return;
      setIsLedgersLoading(true);
      try {
        const res = await axios.get(`${BASE_API_URL}/ledger/${companyId}`);
        if (res.data?.success && Array.isArray(res.data.data)) {
          const formatted = res.data.data.map((l) => ({
            headCode: l.HEAD_CODE,
            HEAD_CODE: l.HEAD_CODE,
            name: l.LedgerName,
            LedgerName: l.LedgerName,
            group: l.GroupName || l.GroupNAME,
            GroupName: l.GroupName || l.GroupNAME,
            groupCode: l.GroupCODE,
            GroupCODE: l.GroupCODE,
            balance: `${Number(l.CL_BALANCE || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} ${l.OpenBlType || "Dr"}`,
            CL_BALANCE: l.CL_BALANCE,
            OpenBlType: l.OpenBlType,
          }));
          setLedgers(formatted);
        }
      } catch (err) {
        console.warn("Could not load ledgers from API:", err);
      } finally {
        setIsLedgersLoading(false);
      }
    };
    fetchLedgers();
  }, [BASE_API_URL, companyId]);

  // 3. Preload all Next Voucher Numbers for ALL Voucher Types in 1 call for rapid instant switching
  const fetchAllNextVoucherNumbers = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await axios.get(`${BASE_API_URL}/vouchers/next-numbers/${companyId}`);
      if (res.data?.success && res.data.data) {
        const map = res.data.data;
        setVoucherNumbersMap(map);
        if (!isModifyMode && currentVTypeNum && map[currentVTypeNum] && !loadedVoucherId) {
          setVoucherNo(String(map[currentVTypeNum]));
        }
      }
    } catch (err) {
      console.warn("Could not fetch all next voucher numbers:", err);
    }
  }, [BASE_API_URL, companyId, currentVTypeNum, isModifyMode, loadedVoucherId]);

  useEffect(() => {
    if (!isModifyMode) {
      fetchAllNextVoucherNumbers();
    }
  }, [fetchAllNextVoucherNumbers, isModifyMode]);

  // 4. Fetch list of existing vouchers for modification
  const fetchExistingVouchers = useCallback(async () => {
    if (!companyId) return;
    setIsLoadingVouchersList(true);
    try {
      const res = await axios.get(`${BASE_API_URL}/vouchers/${companyId}`);
      if (res.data?.success && Array.isArray(res.data.vouchers)) {
        // Exclude sales vouchers since sales vouchers are generated & managed from Invoices
        const filtered = res.data.vouchers.filter((v) => !isSalesType(v));
        setExistingVouchers(filtered);
      }
    } catch (err) {
      console.warn("Could not load existing vouchers:", err);
    } finally {
      setIsLoadingVouchersList(false);
    }
  }, [BASE_API_URL, companyId]);

  useEffect(() => {
    if (isModifyMode) {
      fetchExistingVouchers();
    }
  }, [isModifyMode, fetchExistingVouchers]);

  // 5. Load voucher details for modification
  const loadVoucherForModification = useCallback(
    async (vId) => {
      if (!vId) return;
      const key = `id-${String(vId)}`;
      if (loadedKeyRef.current === key) return;
      loadedKeyRef.current = key;

      setIsLoadingVoucher(true);
      try {
        const res = await axios.get(`${BASE_API_URL}/vouchers/details/${vId}`);
        if (res.data?.success && res.data.voucher) {
          const v = res.data.voucher;
          setLoadedVoucherId(v.voucherId);
          setSelectedVoucherId(String(v.voucherId));
          setLoadedVoucherData(v);

          // Find matching voucher type
          const matched = voucherTypes.find(
            (t) =>
              t.vtype === v.vtype ||
              t.id === v.vtype ||
              (t.name && v.vtypeName && t.name.toLowerCase() === v.vtypeName.toLowerCase())
          );
          if (matched) {
            setVoucherType(matched.id);
          }

          setVoucherNo(String(v.voucherNo || ""));
          setReferenceNo(v.referenceNo || "");

          if (v.voucherDate) {
            const dStr = typeof v.voucherDate === "string" ? v.voucherDate.slice(0, 10) : "";
            if (dStr) setVoucherDate(dStr);
          }

          setNarration(v.narration || "");

          if (Array.isArray(v.entries) && v.entries.length > 0) {
            const loadedRows = v.entries.map((entry, idx) => {
              const isDebit = Number(entry.debit) > 0;
              const ledgerName =
                entry.ledgerName && entry.ledgerName !== "Unknown Ledger"
                  ? entry.ledgerName
                  : entry.particular || entry.ledgerName || "";
              return {
                id: entry.id || Date.now() + idx,
                type: isDebit ? "Dr" : "Cr",
                headCode: entry.headCode,
                ledger: ledgerName,
                dr: isDebit ? String(entry.debit) : "",
                cr: !isDebit ? String(entry.credit) : "",
              };
            });

            // Ensure at least 2 rows
            while (loadedRows.length < 2) {
              loadedRows.push({
                id: Date.now() + loadedRows.length,
                type: loadedRows[0]?.type === "Dr" ? "Cr" : "Dr",
                headCode: null,
                ledger: "",
                dr: "",
                cr: "",
              });
            }
            setRows(loadedRows);

            // Snapshot clean initial state to track if changes are made before enabling Update
            const loadedVType = matched ? matched.id : v.vtype;
            const loadedDateStr = v.voucherDate
              ? typeof v.voucherDate === "string"
                ? v.voucherDate.slice(0, 10)
                : ""
              : "";
            setInitialVoucherSnapshot({
              voucherType: loadedVType,
              voucherNo: String(v.voucherNo || ""),
              referenceNo: String(v.referenceNo || ""),
              voucherDate: loadedDateStr,
              narration: String(v.narration || ""),
              rows: loadedRows.map((r) => ({
                type: r.type,
                ledger: (r.ledger || "").trim(),
                dr: parseFloat(r.dr) || 0,
                cr: parseFloat(r.cr) || 0,
              })),
            });
          }

          // Smoothly sync URL only if needed without triggering reload
          if (isModifyMode && searchParams.get("voucherId") !== String(v.voucherId)) {
            navigate(`/transactions/modify-voucher?voucherId=${v.voucherId}`, { replace: true });
          }
        } else {
          loadedKeyRef.current = null;
          showToast.error("Voucher not found");
        }
      } catch (err) {
        loadedKeyRef.current = null;
        console.error("Error loading voucher for modification:", err);
        showToast.error("Failed to load voucher details");
      } finally {
        setIsLoadingVoucher(false);
      }
    },
    [BASE_API_URL, voucherTypes, isModifyMode, navigate, searchParams]
  );

  // 6. Search and load voucher by Voucher No or Reference No
  const loadVoucherByNumber = useCallback(
    async (vNo) => {
      if (!vNo || !companyId) return;
      const key = `no-${String(vNo)}`;
      if (loadedKeyRef.current === key) return;
      loadedKeyRef.current = key;

      setIsLoadingVoucher(true);
      try {
        const res = await axios.get(`${BASE_API_URL}/vouchers/${companyId}`, {
          params: { search: vNo },
        });
        if (res.data?.success && Array.isArray(res.data.vouchers) && res.data.vouchers.length > 0) {
          const found =
            res.data.vouchers.find(
              (v) =>
                String(v.voucherNo).toLowerCase() === String(vNo).toLowerCase() ||
                String(v.referenceNo).toLowerCase() === String(vNo).toLowerCase()
            ) || res.data.vouchers[0];

          if (found?.voucherId) {
            loadedKeyRef.current = null; // Let loadVoucherForModification take over
            loadVoucherForModification(found.voucherId);
            return;
          }
        }
        loadedKeyRef.current = null;
        showToast.warn(`Voucher "${vNo}" not found`);
      } catch (err) {
        loadedKeyRef.current = null;
        console.error("Error finding voucher by number:", err);
        showToast.error(`Could not find voucher "${vNo}"`);
      } finally {
        setIsLoadingVoucher(false);
      }
    },
    [BASE_API_URL, companyId, loadVoucherForModification]
  );

  // 7. Auto-load from URL query parameters (voucherId, edit, voucherNo)
  useEffect(() => {
    const vId = searchParams.get("voucherId") || searchParams.get("edit");
    const vNo = searchParams.get("voucherNo");
    if (vId) {
      loadVoucherForModification(vId);
    } else if (vNo) {
      loadVoucherByNumber(vNo);
    }
  }, [searchParams, loadVoucherForModification, loadVoucherByNumber]);

  // Reset selected voucher in modify mode or switch
  const handleClearSelection = () => {
    loadedKeyRef.current = null;
    setSelectedVoucherId("");
    setLoadedVoucherId(null);
    setLoadedVoucherData(null);
    setInitialVoucherSnapshot(null);
    setReferenceNo("");
    const today = new Date().toISOString().slice(0, 10);
    setVoucherDate(today);
    setNarration("");
    const defaultType = voucherTypes[0]?.id || INITIAL_VOUCHER_STATE.voucherType;
    setVoucherType(defaultType);
    setRows(createInitialRows(defaultType));
    if (!isModifyMode) {
      const vtypeNum = voucherTypes[0]?.vtype || 2;
      if (voucherNumbersMap && voucherNumbersMap[vtypeNum]) {
        setVoucherNo(String(voucherNumbersMap[vtypeNum]));
      }
    } else {
      navigate("/transactions/modify-voucher", { replace: true });
    }
  };

  // 8. Auto-reset form when navigating between Modify Mode and New Voucher Entry mode
  const prevIsModifyModeRef = useRef(isModifyMode);
  useEffect(() => {
    if (prevIsModifyModeRef.current !== isModifyMode) {
      prevIsModifyModeRef.current = isModifyMode;

      // Always clear loaded voucher state when navigating between modes
      loadedKeyRef.current = null;
      setLoadedVoucherId(null);
      setLoadedVoucherData(null);
      setInitialVoucherSnapshot(null);
      setSelectedVoucherId("");
      setReferenceNo("");
      setNarration("");
      const today = new Date().toISOString().slice(0, 10);
      setVoucherDate(today);

      const defaultType = voucherTypes[0]?.id || INITIAL_VOUCHER_STATE.voucherType;
      setVoucherType(defaultType);
      setRows(createInitialRows(defaultType));

      if (!isModifyMode) {
        // In new voucher entry mode, retrieve fresh next voucher number
        const vtypeNum = voucherTypes[0]?.vtype || 2;
        if (voucherNumbersMap && voucherNumbersMap[vtypeNum]) {
          setVoucherNo(String(voucherNumbersMap[vtypeNum]));
        }
        fetchAllNextVoucherNumbers();
      } else {
        setVoucherNo("");
      }
    }
  }, [isModifyMode, voucherTypes, voucherNumbersMap, fetchAllNextVoucherNumbers]);

  // Format "As on: 29-Sep-2026"
  const formattedAsOnDate = useMemo(() => {
    try {
      const d = new Date(voucherDate || new Date());
      if (isNaN(d.getTime())) return "Today";
      const day = String(d.getDate()).padStart(2, "0");
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${day}-${monthNames[d.getMonth()]}-${d.getFullYear()}`;
    } catch {
      return "Today";
    }
  }, [voucherDate]);

  // Calculate live Dr & Cr Totals
  const { totalDr, totalCr, isBalanced, difference } = useMemo(() => {
    let drSum = 0;
    let crSum = 0;

    rows.forEach((r) => {
      const drVal = parseFloat(r.dr) || 0;
      const crVal = parseFloat(r.cr) || 0;
      drSum += drVal;
      crSum += crVal;
    });

    const diff = Math.abs(drSum - crSum);
    const balanced = drSum > 0 && Math.round(drSum * 100) === Math.round(crSum * 100);

    return {
      totalDr: drSum,
      totalCr: crSum,
      isBalanced: balanced,
      difference: diff,
    };
  }, [rows]);

  // Track whether any modifications have been made to the loaded voucher
  const isFormDirty = useMemo(() => {
    if (!isModifyMode) {
      return true; // When creating new vouchers, always allow saving if valid
    }
    if (!initialVoucherSnapshot) {
      return false; // In modify mode, nothing loaded yet
    }

    // Check header fields
    if (String(voucherType) !== String(initialVoucherSnapshot.voucherType)) return true;
    if (String(voucherNo || "").trim() !== String(initialVoucherSnapshot.voucherNo || "").trim()) return true;
    if (String(referenceNo || "").trim() !== String(initialVoucherSnapshot.referenceNo || "").trim()) return true;
    if (String(voucherDate || "").slice(0, 10) !== String(initialVoucherSnapshot.voucherDate || "").slice(0, 10)) return true;
    if (String(narration || "").trim() !== String(initialVoucherSnapshot.narration || "").trim()) return true;

    // Filter meaningful rows (those with a ledger or an amount)
    const currentActiveRows = rows.filter(
      (r) => r.ledger?.trim() || parseFloat(r.dr) > 0 || parseFloat(r.cr) > 0
    );
    const initialActiveRows = initialVoucherSnapshot.rows.filter(
      (r) => r.ledger?.trim() || r.dr > 0 || r.cr > 0
    );

    if (currentActiveRows.length !== initialActiveRows.length) return true;

    for (let i = 0; i < currentActiveRows.length; i++) {
      const cur = currentActiveRows[i];
      const init = initialActiveRows[i];

      if (cur.type !== init.type) return true;
      if (String(cur.ledger || "").trim().toLowerCase() !== String(init.ledger || "").trim().toLowerCase()) return true;

      const curDr = parseFloat(cur.dr) || 0;
      const initDr = parseFloat(init.dr) || 0;
      if (Math.abs(curDr - initDr) > 0.001) return true;

      const curCr = parseFloat(cur.cr) || 0;
      const initCr = parseFloat(init.cr) || 0;
      if (Math.abs(curCr - initCr) > 0.001) return true;
    }

    return false;
  }, [
    isModifyMode,
    initialVoucherSnapshot,
    voucherType,
    voucherNo,
    referenceNo,
    voucherDate,
    narration,
    rows,
  ]);

  // Save validation
  const { canSave, saveDisabledReason } = useMemo(() => {
    if (isModifyMode && !loadedVoucherId) {
      return { canSave: false, saveDisabledReason: "Please select a voucher to modify" };
    }
    if (isModifyMode && !isFormDirty) {
      return { canSave: false, saveDisabledReason: "No changes detected to update" };
    }
    if (totalDr === 0 && totalCr === 0) {
      return { canSave: false, saveDisabledReason: "Please enter debit and credit amounts" };
    }
    if (!isBalanced) {
      return {
        canSave: false,
        saveDisabledReason: `Total Dr (₹${totalDr.toFixed(2)}) & Total Cr (₹${totalCr.toFixed(2)}) must be equal to save.`,
      };
    }
    const missingLedger = rows.some(
      (r) => (parseFloat(r.dr) > 0 || parseFloat(r.cr) > 0) && !r.ledger?.trim()
    );
    if (missingLedger) {
      return { canSave: false, saveDisabledReason: "Please select a Ledger Account for all rows with amounts" };
    }

    return { canSave: true, saveDisabledReason: "" };
  }, [isModifyMode, loadedVoucherId, isFormDirty, totalDr, totalCr, isBalanced, rows]);

  // Rapid instantaneous switch of voucher type with cached voucher number (0ms delay)
  const handleVoucherTypeChange = useCallback(
    (typeId) => {
      if (typeId === voucherType) return;
      setVoucherType(typeId);
      // Reset rows to clean initial state for the new voucher type (Receipt starts with Cr, etc.)
      setRows(createInitialRows(typeId));
      const target = voucherTypes.find(
        (v) =>
          v.id === typeId ||
          v.vtype === typeId ||
          (typeof typeId === "string" && v.name?.toLowerCase() === typeId.toLowerCase())
      );
      const vtypeCode = target?.vtype;
      if (vtypeCode && voucherNumbersMap[vtypeCode]) {
        setVoucherNo(String(voucherNumbersMap[vtypeCode]));
      }
    },
    [voucherType, voucherTypes, voucherNumbersMap]
  );

  // Keyboard shortcut listener for F1 to F9
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.ctrlKey && !e.altKey && !e.metaKey) {
        const matching = voucherTypes.find((v) => v.code === e.key || v.key === e.key);
        if (matching) {
          e.preventDefault();
          handleVoucherTypeChange(matching.id);
          showToast.info(`Switched to ${matching.name} Voucher (${matching.code || matching.key})`);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [voucherTypes, handleVoucherTypeChange]);

// Live balancing helper: auto-calculates Dr/Cr difference and auto-creates/updates balancing row immediately
function rebalanceRows(rowsList, changedIndex) {
  const list = rowsList.map((r) => ({ ...r }));

  // Find the last row with an assigned ledger or the row currently being edited
  let lastFixedIndex = -1;
  for (let i = list.length - 1; i >= 0; i--) {
    if (list[i].ledger?.trim() || i === changedIndex) {
      lastFixedIndex = i;
      break;
    }
  }

  if (lastFixedIndex === -1) lastFixedIndex = 0;

  // Sum Dr and Cr up to lastFixedIndex
  let sumDr = 0;
  let sumCr = 0;
  for (let i = 0; i <= lastFixedIndex; i++) {
    sumDr += parseFloat(list[i].dr) || 0;
    sumCr += parseFloat(list[i].cr) || 0;
  }

  sumDr = Math.round(sumDr * 100) / 100;
  sumCr = Math.round(sumCr * 100) / 100;

  // If no amounts entered anywhere, keep minimum 2 rows
  if (sumDr === 0 && sumCr === 0) {
    const trimmed = list.filter((r, idx) => idx <= lastFixedIndex || r.ledger?.trim());
    while (trimmed.length < 2) {
      const prevType = trimmed[trimmed.length - 1]?.type === "Dr" ? "Cr" : "Dr";
      trimmed.push({
        id: Date.now() + trimmed.length,
        type: prevType,
        headCode: null,
        ledger: "",
        dr: "",
        cr: "",
      });
    }
    return trimmed;
  }

  const diff = Math.round(Math.abs(sumDr - sumCr) * 100) / 100;

  // CASE 1: Balanced (diff === 0)
  if (diff === 0) {
    // Remove any trailing rows that have no ledger (keep min 2 rows)
    const trimmed = list.filter((r, idx) => idx <= lastFixedIndex || r.ledger?.trim());
    while (trimmed.length < 2) {
      const prevType = trimmed[trimmed.length - 1]?.type === "Dr" ? "Cr" : "Dr";
      trimmed.push({
        id: Date.now() + trimmed.length,
        type: prevType,
        headCode: null,
        ledger: "",
        dr: "",
        cr: "",
      });
    }
    return trimmed;
  }

  // CASE 2: Imbalanced (diff > 0)
  const deficitSide = sumDr > sumCr ? "Cr" : "Dr";
  const formattedDiff = diff % 1 === 0 ? String(diff) : diff.toFixed(2);

  // If we only have 2 rows and Row 1 has no ledger, and user just edited Row 0
  if (list.length === 2 && !list[1].ledger?.trim() && changedIndex === 0) {
    list[1] = {
      ...list[1],
      type: deficitSide,
      dr: deficitSide === "Dr" ? formattedDiff : "",
      cr: deficitSide === "Cr" ? formattedDiff : "",
    };
    return list;
  }

  // Check if a row already exists after lastFixedIndex without a ledger
  const autoRowIndex = list.findIndex(
    (r, idx) => idx > lastFixedIndex && !r.ledger?.trim()
  );

  if (autoRowIndex !== -1) {
    // Update existing auto-row
    list[autoRowIndex] = {
      ...list[autoRowIndex],
      type: deficitSide,
      dr: deficitSide === "Dr" ? formattedDiff : "",
      cr: deficitSide === "Cr" ? formattedDiff : "",
    };
    return list.slice(0, autoRowIndex + 1);
  } else {
    // Automatically CREATE and append new row for balance immediately!
    list.push({
      id: Date.now(),
      type: deficitSide,
      headCode: null,
      ledger: "",
      dr: deficitSide === "Dr" ? formattedDiff : "",
      cr: deficitSide === "Cr" ? formattedDiff : "",
    });
    return list;
  }
}

  // Table row change with instant auto-balancing
  const handleRowChange = (index, field, value, extraHeadCode = null) => {
    setRows((prev) => {
      const copy = prev.map((r) => ({ ...r }));
      if (!copy[index]) return prev;

      if (field === "type") {
        const newType = value;
        copy[index].type = newType;
        copy[index].ledger = "";
        copy[index].headCode = null;
        if (newType === "Dr") {
          if (copy[index].cr) copy[index].dr = copy[index].cr;
          copy[index].cr = "";
        } else if (newType === "Cr") {
          if (copy[index].dr) copy[index].cr = copy[index].dr;
          copy[index].dr = "";
        }
      } else if (field === "dr") {
        copy[index].type = "Dr";
        copy[index].dr = value;
        copy[index].cr = "";
      } else if (field === "cr") {
        copy[index].type = "Cr";
        copy[index].cr = value;
        copy[index].dr = "";
      } else if (field === "ledger") {
        copy[index].ledger = value;
        if (extraHeadCode) {
          copy[index].headCode = extraHeadCode;
        } else {
          const found = ledgers.find((l) => (l.name || l.LedgerName) === value);
          if (found) {
            copy[index].headCode = found.headCode || found.HEAD_CODE;
          }
        }
      } else {
        copy[index][field] = value;
      }

      // When amount is updated, trigger instant live auto-balancing
      if (field === "dr" || field === "cr") {
        return rebalanceRows(copy, index);
      }

      return copy;
    });
  };

  const handleAddRow = (preferredType) => {
    const isReceipt = String(voucherType).toLowerCase().trim().replace(/[\s-]+/g, "_") === "receipt";
    const defaultType = preferredType && typeof preferredType === "string" ? preferredType : (isReceipt ? "Cr" : "Dr");
    setRows((prev) => [
      ...prev,
      { id: Date.now(), type: defaultType, headCode: null, ledger: "", dr: "", cr: "" },
    ]);
  };

  const handleDeleteRow = (index) => {
    if (rows.length <= 2) {
      showToast.warn("A voucher entry requires at least 2 rows (Debit and Credit).");
      return;
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  // Ledger Lookup Modal
  const handleOpenLedgerSearch = (rowIndex) => {
    setActiveRowForSearch(rowIndex);
    setSearchModalOpen(true);
  };

  const handleSelectLedgerFromModal = (ledgerName, headCode) => {
    handleRowChange(activeRowForSearch, "ledger", ledgerName, headCode);
  };

  // Voucher Numbering Config
  const handleOpenSettings = () => {
    showToast.info(`Voucher Numbering is set to Automatic for ${String(voucherType).toUpperCase()}`);
  };

  // Automatic balancing & new row addition (Dr to Cr / Cr to Dr)
  const handleAmountComplete = useCallback((index, field, isEnter = false) => {
    setRows((prev) => {
      const copy = [...prev];
      if (!copy[index]) return prev;

      // Calculate total Dr and Cr
      let sumDr = 0;
      let sumCr = 0;
      copy.forEach((r) => {
        sumDr += parseFloat(r.dr) || 0;
        sumCr += parseFloat(r.cr) || 0;
      });

      sumDr = Math.round(sumDr * 100) / 100;
      sumCr = Math.round(sumCr * 100) / 100;

      if (sumDr === 0 && sumCr === 0) return copy;

      const diff = Math.round(Math.abs(sumDr - sumCr) * 100) / 100;

      if (diff === 0) {
        if (isEnter && index >= 1) {
          setTimeout(() => {
            const narrationEl = document.getElementById("vch_narration_input");
            if (narrationEl) narrationEl.focus();
          }, 60);
        }
        return copy;
      }

      // Deficit side: who needs more amount to balance?
      const deficitSide = sumDr > sumCr ? "Cr" : "Dr";
      const formattedDiff = diff % 1 === 0 ? String(diff) : diff.toFixed(2);

      // Check if there is already an empty row after current index
      const nextEmptyIndex = copy.findIndex(
        (r, i) => i > index && !r.ledger && !parseFloat(r.dr) && !parseFloat(r.cr)
      );

      if (nextEmptyIndex !== -1) {
        copy[nextEmptyIndex] = {
          ...copy[nextEmptyIndex],
          type: deficitSide,
          dr: deficitSide === "Dr" ? formattedDiff : "",
          cr: deficitSide === "Cr" ? formattedDiff : "",
        };
      } else {
        // Automatically add new row for balance
        copy.push({
          id: Date.now(),
          type: deficitSide,
          headCode: null,
          ledger: "",
          dr: deficitSide === "Dr" ? formattedDiff : "",
          cr: deficitSide === "Cr" ? formattedDiff : "",
        });
      }

      return copy;
    });
  }, []);

  // Request Save (saves directly without confirmation modal)
  const handleRequestSave = () => {
    if (!canSave) {
      showToast.error(saveDisabledReason || "IF Dr & Cr Total not equal, cannot save");
      return;
    }
    handleSave();
  };

  // Save Voucher to SQL Database
  const handleSave = async () => {
    if (!canSave) {
      showToast.error(saveDisabledReason || "IF Dr & Cr Total not equal, cannot save");
      return;
    }

    setIsSaving(true);
    try {
      const filledRows = rows.filter(
        (r) => r.ledger && (parseFloat(r.dr) > 0 || parseFloat(r.cr) > 0)
      );

      // Cross-particular logic (Tally-style):
      // Debit entries → PARTICULAR = first Credit ledger name
      // Credit entries → PARTICULAR = first Debit ledger name
      const firstDrRow = filledRows.find((r) => parseFloat(r.dr) > 0);
      const firstCrRow = filledRows.find((r) => parseFloat(r.cr) > 0);
      const firstDrLedgerName = firstDrRow?.ledger || "";
      const firstCrLedgerName = firstCrRow?.ledger || "";

      const validEntries = filledRows.map((r) => {
        const matchedHead =
          r.headCode ||
          ledgers.find((l) => (l.name || l.LedgerName) === r.ledger)?.headCode ||
          ledgers.find((l) => (l.name || l.LedgerName) === r.ledger)?.HEAD_CODE ||
          0;

        const isDrEntry = parseFloat(r.dr) > 0;
        // Dr entry gets first Cr ledger name; Cr entry gets first Dr ledger name
        const crossParticular = isDrEntry ? firstCrLedgerName : firstDrLedgerName;

        return {
          headCode: matchedHead,
          particular: crossParticular || r.ledger,
          debit: parseFloat(r.dr) || 0,
          credit: parseFloat(r.cr) || 0,
        };
      });

      const payload = {
        compId: Number(companyId),
        vtype: currentVTypeNum,
        voucherNo: voucherNo,
        voucherDate: voucherDate,
        valueDate: voucherDate,
        referenceNo: referenceNo && referenceNo.trim() ? referenceNo.trim() : null,
        referenceDate: voucherDate,
        narration: narration && narration.trim() ? narration.trim() : null,
        userId: Number(localStorage.getItem("userId") || 1),
        entries: validEntries,
      };

      let res;
      if (isModifyMode && loadedVoucherId) {
        // Update existing voucher
        try {
          res = await axios.put(`${BASE_API_URL}/vouchers/update/${loadedVoucherId}`, payload);
        } catch (putErr) {
          // Fallback to POST
          res = await axios.post(`${BASE_API_URL}/vouchers/update/${loadedVoucherId}`, payload);
        }

        if (res.data?.success) {
          showToast.success(`Voucher ${voucherNo} modified successfully!`);
          setLoadedVoucherData((prev) => ({
            ...prev,
            amount: totalDr || totalCr,
            totalAmount: totalDr || totalCr,
            narration,
            referenceNo,
          }));
          // Reset dirty state snapshot to current updated values
          setInitialVoucherSnapshot({
            voucherType,
            voucherNo: String(voucherNo || ""),
            referenceNo: String(referenceNo || ""),
            voucherDate: String(voucherDate || "").slice(0, 10),
            narration: String(narration || ""),
            rows: rows.map((r) => ({
              type: r.type,
              ledger: (r.ledger || "").trim(),
              dr: parseFloat(r.dr) || 0,
              cr: parseFloat(r.cr) || 0,
            })),
          });
          fetchExistingVouchers();
        } else {
          showToast.error(res.data?.message || "Failed to update voucher");
        }
      } else {
        // Create new voucher
        res = await axios.post(`${BASE_API_URL}/vouchers/create`, payload);

        if (res.data?.success) {
          showToast.success(`Voucher ${voucherNo} saved successfully!`);
          // Reset rows & narration
          setNarration("");
          setReferenceNo("");
          setRows(createInitialRows(voucherType));

          // Optimistically increment voucher number in local cache immediately
          setVoucherNumbersMap((prev) => {
            const nextVal = String((parseInt(voucherNo, 10) || 0) + 1);
            return { ...prev, [currentVTypeNum]: nextVal };
          });
          setVoucherNo((prev) => String((parseInt(prev, 10) || 0) + 1));

          // Background refresh full map
          fetchAllNextVoucherNumbers();
        } else {
          showToast.error(res.data?.message || "Failed to save voucher");
        }
      }
    } catch (err) {
      console.error("Error saving voucher:", err);
      showToast.error(err.response?.data?.message || err.message || "Failed to save voucher");
    } finally {
      setIsSaving(false);
    }
  };


  // Delete Voucher Confirm Handler (same behavior & modal as invoice delete)
  const handleDeleteVoucherConfirm = async () => {
    if (!loadedVoucherId) return;
    setIsDeleting(true);
    try {
      const res = await axios.delete(
        `${BASE_API_URL}/vouchers/${loadedVoucherId}?compId=${companyId}`
      );
      if (res.data?.success) {
        showToast.delete(`Voucher #${voucherNo} deleted successfully!`);
        // Remove from dropdown list
        setExistingVouchers((prev) =>
          prev.filter((v) => String(v.voucherId) !== String(loadedVoucherId))
        );
        // Clear selection & reset form
        handleClearSelection();
        setDeleteModalOpen(false);
      } else {
        showToast.error(res.data?.message || "Failed to delete voucher");
      }
    } catch (err) {
      console.error("Error deleting voucher:", err);
      showToast.error(
        err.response?.data?.message || err.message || "Failed to delete voucher"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Reset / Cancel Form
  const handleCancel = () => {
    if (isModifyMode) {
      if (loadedVoucherId) {
        if (window.confirm("Do you want to discard your unsaved changes and reload this voucher?")) {
          loadedKeyRef.current = null;
          loadVoucherForModification(loadedVoucherId);
          showToast.info("Reverted changes to voucher.");
        }
      } else {
        handleClearSelection();
      }
      return;
    }

    if (window.confirm("Are you sure you want to reset this voucher entry form?")) {
      const initialType = voucherTypes[0]?.id || INITIAL_VOUCHER_STATE.voucherType;
      setVoucherType(initialType);
      setReferenceNo("");
      const today = new Date().toISOString().slice(0, 10);
      setVoucherDate(today);
      setNarration("");
      setRows(createInitialRows(initialType));
      fetchAllNextVoucherNumbers();
      showToast.info("Form reset to default entry.");
    }
  };

  return (
    <div className="dashboard-wrapper vch_entry_page_wrapper">
      {/* 1. Top Breadcrumb Bar */}
      <div className="vch_breadcrumb_bar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <span>Transactions &gt;</span> {isModifyMode ? "Modify Voucher" : "Voucher Entry"}
          {isModifyMode && (
            <span className="vch_mode_pill">
              <i className="fa-solid fa-pen-to-square"></i> Modification Mode
            </span>
          )}
        </div>
        <div className="vch_as_on_badge">
          <i className="fa-regular fa-calendar-days"></i>
          <span>As on: {formattedAsOnDate}</span>
        </div>
      </div>

      {/* Main Card with Form */}
      <div className="vch_main_card">
        {/* Modify Mode Top Voucher Selector & Search Bar */}
        {isModifyMode && (
          <VoucherModifySelector
            existingVouchers={existingVouchers}
            selectedVoucherId={selectedVoucherId}
            onSelectVoucher={(vId) => {
              setSelectedVoucherId(vId);
              loadVoucherForModification(vId);
            }}
            onOpenSearchModal={() => setSearchVoucherModalOpen(true)}
            onClearSelection={handleClearSelection}
            isLoadingVoucher={isLoadingVoucher}
            isLoadingList={isLoadingVouchersList}
            loadedVoucher={loadedVoucherData}
          />
        )}

        {/* Voucher Type Radio Selector */}
        <VoucherTypeSelector
          voucherTypes={voucherTypes}
          selectedType={voucherType}
          onSelectType={handleVoucherTypeChange}
        />

        {/* 3 Header Fields: Voucher No, Reference No, Voucher Date */}
        <VoucherHeaderFields
          voucherNo={voucherNo}
          onVoucherNoChange={setVoucherNo}
          referenceNo={referenceNo}
          onReferenceNoChange={setReferenceNo}
          voucherDate={voucherDate}
          onVoucherDateChange={setVoucherDate}
          onOpenSettings={handleOpenSettings}
          isModifyMode={isModifyMode}
        />

        {/* Entries Table */}
        <VoucherTable
          rows={rows}
          ledgers={ledgers}
          voucherType={currentVTypeObj?.id || voucherType}
          isModifyMode={isModifyMode}
          onRowChange={handleRowChange}
          onAmountComplete={handleAmountComplete}
          onDeleteRow={handleDeleteRow}
          onAddRow={handleAddRow}
          onOpenLedgerSearch={handleOpenLedgerSearch}
        />

        {/* Table Totals with Add Row & Balance Status */}
        <VoucherTotals
          onAddRow={handleAddRow}
          totalDr={totalDr}
          totalCr={totalCr}
          isBalanced={isBalanced}
          difference={difference}
        />

        {/* Narration */}
        <VoucherNarration
          narration={narration}
          onChange={setNarration}
          onEnterPress={handleRequestSave}
          maxLength={500}
        />

        {/* Footer Actions: Cancel, Delete (in modify mode), and Save/Update */}
        <VoucherFooterActions
          onSave={handleRequestSave}
          onCancel={handleCancel}
          onDelete={() => setDeleteModalOpen(true)}
          canSave={canSave}
          saveDisabledReason={saveDisabledReason}
          isSaving={isSaving}
          isDeleting={isDeleting}
          isModifyMode={isModifyMode}
          hasLoadedVoucher={Boolean(loadedVoucherId)}
        />
      </div>

      {/* Modal: Quick Search Ledger Account */}
      <LedgerSearchModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        onSelectLedger={handleSelectLedgerFromModal}
        currentRowIndex={activeRowForSearch}
        ledgers={ledgers}
        voucherType={currentVTypeObj?.id || voucherType}
        rowType={rows[activeRowForSearch]?.type || "Dr"}
        isModifyMode={isModifyMode}
      />

      {/* Modal: Advanced Search & Filter Voucher to Modify */}
      <VoucherSearchModal
        isOpen={searchVoucherModalOpen}
        onClose={() => setSearchVoucherModalOpen(false)}
        vouchers={existingVouchers}
        voucherTypes={voucherTypes}
        currentLoadedVoucherId={loadedVoucherId}
        onSelectVoucher={(vId) => {
          setSelectedVoucherId(String(vId));
          loadVoucherForModification(vId);
        }}
      />

      {/* Modal: Delete Confirmation (Same as Invoice Delete) */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => !isDeleting && setDeleteModalOpen(false)}
        onConfirm={handleDeleteVoucherConfirm}
        title="Delete Voucher?"
        message="Are you sure you want to delete this voucher? This action cannot be undone."
        itemName={
          loadedVoucherData
            ? `Voucher #${voucherNo} (${currentVTypeObj?.name || ""})`
            : `Voucher #${voucherNo}`
        }
        loading={isDeleting}
      />
    </div>
  );
}
